<template>
  <v-container fluid class="px-0">
    <v-card elevation="2" class="mx-auto">
      <v-card-title class="white--text blue-grey darken-2 py-4 px-6 d-flex align-center">
        <v-icon left color="white">mdi-car</v-icon>
        {{ modoEdicao ? 'Editar Veículo' : 'Cadastrar Novo Veículo' }}
        <v-spacer></v-spacer>
        <v-btn
            color="white"
            outlined
            @click="$router.push({ name: 'cadastros-veiculos' })"
        >
          <v-icon>mdi-arrow-left</v-icon>
          <span class="ml-2 d-none d-sm-flex">Voltar</span>
        </v-btn>
      </v-card-title>

      <v-divider/>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="formularioValido" @submit.prevent="salvarVeiculo">
          <v-row>
            <!-- Seção: Informações do Veículo -->
            <v-col cols="12">
              <div class="section-header">
                <v-icon left color="primary">mdi-car-info</v-icon>
                <span class="text-h6 font-weight-medium">Informações do Veículo</span>
              </div>
            </v-col>

            <!-- Tipo de Veículo -->
            <v-col cols="12" md="6" class="pb-5">
              <v-autocomplete
                  v-model="dados.idtipo"
                  :items="tiposVeiculo"
                  :rules="[rules.required]"
                  :loading="carregandoTipos"
                  item-text="tipo"
                  item-value="idtipo"
                  label="Tipo de Veículo *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-car-multiple"
                  clearable
                  @change="onTipoVeiculoChange"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhum tipo encontrado</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Nome do Veículo -->
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                  v-model="dados.nomeveiculo"
                  :rules="[rules.required, rules.maxLength(100)]"
                  label="Nome do Veículo *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-car"
                  placeholder="Ex: VW 15.190 CONST. CARROCERIA"
                  counter="100"
              />
            </v-col>

            <!-- Placa -->
            <v-col cols="12" md="6" v-if="placaObrigatoria" class="pb-5">
              <v-text-field
                  v-model="dados.placa"
                  :rules="placaObrigatoria ? [rules.required, rules.placa] : [rules.placa]"
                  :label="placaObrigatoria ? 'Placa *' : 'Placa'"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-license"
                  placeholder="Ex: ABC1234 ou ABC1D23"
                  @input="formatarPlaca"
                  maxlength="8"
                  :class="{ 'campo-obrigatorio': placaObrigatoria }"
              />
            </v-col>

            <!-- Marca -->
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                  v-model="dados.marca"
                  :rules="[rules.required, rules.maxLength(50)]"
                  label="Marca *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-factory"
                  placeholder="Ex: Volkswagen, Mercedes-Benz, Ford"
                  counter="50"
              />
            </v-col>

            <!-- Modelo -->
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                  v-model="dados.modelo"
                  :rules="[rules.required, rules.maxLength(50)]"
                  label="Modelo *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-car-side"
                  placeholder="Ex: Axor, Atego, Constellation"
                  counter="50"
              />
            </v-col>

            <!-- Cor -->
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                  v-model="dados.cor"
                  :rules="[rules.required, rules.maxLength(30)]"
                  label="Cor *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-palette"
                  placeholder="Ex: Branco, Preto, Prata"
                  counter="30"
              />
            </v-col>

            <!-- Ano de Fabricação -->
            <v-col cols="12" md="6">
              <v-text-field
                  v-model="dados.anofabricacao"
                  :rules="[rules.required, rules.ano]"
                  label="Ano de Fabricação *"
                  type="number"
                  :min="1900"
                  :max="new Date().getFullYear() + 1"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-calendar"
                  placeholder="Ex: 2020"
              />
            </v-col>

            <!-- Seção: Informações Técnicas -->
            <v-col cols="12" class="pt-6">
              <div class="section-header">
                <v-icon left color="primary">mdi-cog</v-icon>
                <span class="text-h6 font-weight-medium">Informações Técnicas</span>
              </div>
            </v-col>

            <!-- Tipo de Combustível -->
            <v-col cols="12" md="6">
              <v-autocomplete
                  v-model="dados.idproduto"
                  :items="produtos"
                  :rules="[rules.required]"
                  :loading="carregandoProdutos"
                  item-text="produto"
                  item-value="idproduto"
                  label="Tipo de Combustível *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-fuel"
                  clearable
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhum combustível encontrado</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Empresa -->
            <v-col cols="12" md="6">
              <v-autocomplete
                  v-model="dados.idfilial"
                  :items="filiaisFiltro"
                  :loading="carregandoEmpresas"
                  :rules="[rules.required]"
                  item-text="filial"
                  item-value="idfilial"
                  label="Filial *"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-office-building"
                  clearable
                  :disabled="!podeAlterarFilial"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhuma filial encontrada</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                  v-if="false"
                  v-model="dados.idempresa"
                  :items="empresas"
                  :loading="carregandoEmpresas"
                  item-text="nome"
                  item-value="id"
                  label="Empresa"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-domain"
                  clearable
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Nenhuma empresa encontrada</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- RENAVAM -->
            <v-col cols="12" md="6" v-if="renavamObrigatorio">
              <v-text-field
                  v-model="dados.renavam"
                  :rules="renavamObrigatorio ? [rules.required, rules.renavam] : [rules.renavam]"
                  :label="renavamObrigatorio ? 'RENAVAM *' : 'RENAVAM'"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-file-document-outline"
                  placeholder="Ex: 12345678901"
                  maxlength="11"
                  :class="{ 'campo-obrigatorio': renavamObrigatorio }"
              />
            </v-col>

            <!-- Chassi -->
            <v-col cols="12" md="6" v-if="chassiObrigatorio">
              <v-text-field
                  v-model="dados.chassi"
                  :rules="chassiObrigatorio ? [rules.required, rules.chassi] : [rules.chassi]"
                  :label="chassiObrigatorio ? 'Chassi *' : 'Chassi'"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-barcode"
                  placeholder="Ex: 9BWZZZ377VT004251"
                  maxlength="17"
                  @input="formatarChassi"
                  :class="{ 'campo-obrigatorio': chassiObrigatorio }"
              />
            </v-col>

            <!-- Hodômetro -->
            <v-col cols="12" md="6" v-if="odometroObrigatorio">
              <v-text-field
                  v-model.number="dados.odometro"
                  :rules="odometroObrigatorio ? [rules.required, rules.positivo, rules.inteiro] : [rules.positivo, rules.inteiro]"
                  label="Hodômetro (KM) *"
                  suffix="KM"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-speedometer"
                  placeholder="Ex: 150000"
                  maxlength="6"
              />
            </v-col>

            <!-- Campo vazio para alinhamento -->
            <v-col cols="12" md="6" class="d-none d-md-block"></v-col>

            <!-- Observações -->
            <v-col cols="12">
              <v-textarea
                  v-model="dados.observacao"
                  :rules="[rules.maxLength(500)]"
                  label="Observações"
                  rows="3"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-note-text-outline"
                  counter="500"
                  placeholder="Informações adicionais sobre o veículo..."
              />
            </v-col>

            <!-- Resumo do Cadastro -->
            <v-col cols="12" v-if="mostrarResumo">
              <v-card outlined class="resumo-card">
                <v-card-subtitle class="pb-2 d-flex align-center">
                  <v-icon left color="info">mdi-information</v-icon>
                  <span class="font-weight-medium">Resumo do {{ modoEdicao ? 'Veículo' : 'Cadastro' }}</span>
                </v-card-subtitle>
                <v-card-text class="pt-0">
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Tipo</div>
                      <div class="font-weight-medium">{{ tipoVeiculoSelecionado }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Veículo</div>
                      <div class="font-weight-medium">{{ dados.nomeveiculo || '-' }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Placa</div>
                      <div class="font-weight-medium">{{ dados.placa || '-' }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Marca/Modelo</div>
                      <div class="font-weight-medium">{{ marcaModelo }}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-caption grey--text text--darken-1">Combustível</div>
                      <div class="font-weight-medium">{{ combustivelSelecionado }}</div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Botões de Ação -->
            <v-col cols="12" class="d-flex justify-end align-center pt-6" style="gap: 12px;">
              <v-btn
                  color="grey"
                  outlined
                  elevation="0"
                  @click="limparFormulario"
                  :disabled="salvando"
              >
                <v-icon left>mdi-eraser</v-icon>
                Limpar
              </v-btn>

              <v-btn
                  color="blue-grey darken-2"
                  class="white--text"
                  elevation="2"
                  type="submit"
                  :loading="salvando"
                  :disabled="salvando"
                  x-large
              >
                <v-icon left>mdi-check</v-icon>
                {{ salvando ? 'Salvando...' : (modoEdicao ? 'Atualizar Veículo' : 'Cadastrar Veículo') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Snackbar para feedback -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        bottom
    >
      <v-icon left color="white">{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            color="white"
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import mixinFilial from "../../../util/mixinFilial";

export default {
  mixins: [mixinFilial],
  components: {},
  data() {
    return {
      formularioValido: false,
      salvando: false,
      carregandoProdutos: false,
      carregandoEmpresas: false,
      carregandoTipos: false,
      modoEdicao: false,

      dados: {
        nomeveiculo: null,
        modelo: null,
        marca: null,
        placa: null,
        cor: null,
        anofabricacao: null,
        observacao: null,
        renavam: null,
        chassi: null,
        odometro: null,
        idfilial: null,
        idveiculo: 0,
        idproduto: null,
        idtipo: null
      },

      produtos: [],
      empresas: [],
      tiposVeiculo: [],

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 4000
      },

      // Regras de validação
      rules: {
        required: value => !!value || 'Campo obrigatório',
        positivo: value => !value || value > 0 || 'Valor deve ser maior que zero',
        inteiro: value => !value || Number.isInteger(Number(value)) || 'Deve ser um número inteiro',
        maxLength: (max) => value => !value || value.length <= max || `Máximo ${max} caracteres`,
        placa: value => {
          if (!value && this.placaObrigatoria) return 'Campo obrigatório';
          if (!value) return true;
          // Formatos: ABC-1234 ou ABC1D23
          const placaAntiga = /^[A-Z]{3}-?[0-9]{4}$/;
          const placaMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;
          const placaLimpa = value.replace(/[^A-Z0-9]/g, '');

          if (!placaAntiga.test(value) && !placaMercosul.test(placaLimpa)) {
            return 'Formato inválido. Use ABC-1234 ou ABC1D23';
          }
          return true;
        },
        renavam: value => {
          if (!value && this.renavamObrigatorio) return 'Campo obrigatório';
          if (!value) return true;
          if (!/^[0-9]{11}$/.test(value)) {
            return 'RENAVAM deve ter 11 dígitos';
          }
          return true;
        },
        chassi: value => {
          if (!value && this.chassiObrigatorio) return 'Campo obrigatório';
          if (!value) return true;
          if (value.length !== 17) {
            return 'Chassi deve ter 17 caracteres';
          }
          if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(value)) {
            return 'Chassi contém caracteres inválidos';
          }
          return true;
        },
        ano: value => {
          if (!value) return true;
          const anoAtual = new Date().getFullYear();
          if (value < 1900 || value > anoAtual + 1) {
            return `Ano deve estar entre 1900 e ${anoAtual + 1}`;
          }
          return true;
        }
      }
    };
  },

  computed: {
    ...mapState(["backendUrl", "usuario"]),

    filiaisFiltro() {
      if (this.filiais.lista) {
        return this.filiais.lista.filter((v) => {
          return (
              v.idfilial !== 6 &&
              v.idfilial !== 12 &&
              v.idfilial !== 25 &&
              v.idfilial !== 29 &&
              v.idfilial !== 33 &&
              v.idfilial !== 37
          );
        });
      } else {
        return [];
      }
    },

    mostrarResumo() {
      return this.dados.nomeveiculo && this.dados.placa && this.dados.marca;
    },

    marcaModelo() {
      const marca = this.dados.marca || '';
      const modelo = this.dados.modelo || '';
      return marca && modelo ? `${marca} ${modelo}` : marca || modelo || '-';
    },

    combustivelSelecionado() {
      const produto = this.produtos.find(p => p.idproduto === this.dados.idproduto);
      return produto ? produto.produto : '-';
    },

    tipoVeiculoSelecionado() {
      const tipo = this.tiposVeiculo.find(t => t.idtipo === this.dados.idtipo);
      return tipo ? tipo.tipo : '-';
    },

    tipoSelecionado() {
      return this.tiposVeiculo.find(t => t.idtipo === this.dados.idtipo);
    },

    placaObrigatoria() {
      return this.tipoSelecionado && this.tipoSelecionado.flagregistro;
    },

    renavamObrigatorio() {
      return this.tipoSelecionado && this.tipoSelecionado.flagregistro;
    },

    chassiObrigatorio() {
      return this.tipoSelecionado && this.tipoSelecionado.flagregistro;
    },

    odometroObrigatorio() {
      return this.tipoSelecionado && this.tipoSelecionado.flagregistro;
    }
  },

  methods: {
    formatarPlaca(value) {
      if (!value) return;

      // Remove caracteres especiais
      let placaLimpa = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

      // Formata placa antiga (ABC1234 -> ABC-1234)
      if (placaLimpa.length === 7 && /^[A-Z]{3}[0-9]{4}$/.test(placaLimpa)) {
        this.dados.placa = `${placaLimpa.substr(0, 3)}-${placaLimpa.substr(3)}`;
      } else {
        this.dados.placa = placaLimpa;
      }
    },

    formatarChassi(value) {
      if (!value) return;
      this.dados.chassi = value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    },

    onTipoVeiculoChange() {
      const tipoSelecionado = this.tipoSelecionado;
      if (tipoSelecionado && !tipoSelecionado.flagregistro) {

        this.dados.placa = null;
        this.dados.renavam = null;
        this.dados.chassi = null;
      }

      // Força revalidação do formulário
      // this.$nextTick(() => {
      //   if (this.$refs.form) {
      //     this.$refs.form.validate();
      //   }
      // });
    },

    podeAlterarFilial() {
      return this.permissao("filial_listar")
    },

    async salvarVeiculo() {
      if (!this.$refs.form.validate()) {
        this.mostrarSnackbar('Por favor, corrija os erros no formulário', 'error');
        return;
      }

      this.salvando = true;

      try {
        const payload = {
          ...this.dados,
          acao: this.modoEdicao ? "U" : "I",
        };

        await axios.post(`${this.backendUrl}tms/dba/veiculo/inserir`, payload);

        const mensagem = this.modoEdicao
            ? 'Veículo atualizado com sucesso!'
            : 'Veículo cadastrado com sucesso!';

        this.mostrarSnackbar(mensagem, 'success');

        if (!this.modoEdicao) {
          this.limparFormulario();
        }

      } catch (error) {
        console.error('Erro ao salver veículo:', error);
        const mensagem = error.response?.data?.message || 'Erro ao salvar veículo. Tente novamente.';
        this.mostrarSnackbar(mensagem, 'error');
      } finally {
        this.salvando = false;
      }
    },

    limparFormulario() {
      this.$refs.form.reset();
      this.dados = {
        nomeveiculo: null,
        modelo: null,
        marca: null,
        placa: null,
        cor: null,
        anofabricacao: null,
        observacao: null,
        renavam: null,
        chassi: null,
        odometro: null,
        idfilial: null,
        idveiculo: 0,
        idproduto: null,
        idtipo: null
      };
    },

    async listarProdutos() {
      this.carregandoProdutos = true;
      try {
        const res = await axios.post(`${this.backendUrl}tms/produto/listar`, {
          limit: 9999,
          offset: 0,
          busca: null,
        });

        this.produtos = res.data || [];
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        this.mostrarSnackbar('Erro ao carregar combustíveis', 'error');
      } finally {
        this.carregandoProdutos = false;
      }
    },

    async listarTiposVeiculo() {
      this.carregandoTipos = true;
      try {
        const res = await axios.post(`${this.backendUrl}tms/tipo/veiculo/listar`, {});
        this.tiposVeiculo = res.data || [];
      } catch (error) {
        console.error('Erro ao carregar tipos de veículo:', error);
        this.mostrarSnackbar('Erro ao carregar tipos de veículo', 'error');
      } finally {
        this.carregandoTipos = false;
      }
    },

    async carregarVeiculo(id) {
      try {
        const res = await axios.post(`${this.backendUrl}tms/veiculo/listar`, {
          idveiculo: id
        });

        if (res.data) {
          this.dados = { ...this.dados, ...res.data[0] };
          this.modoEdicao = true;
        }
      } catch (error) {
        console.error('Erro ao carregar veículo:', error);
        this.mostrarSnackbar('Erro ao carregar dados do veículo', 'error');
      }
    },

    mostrarSnackbar(message, type = 'info') {
      const config = {
        success: { color: 'success', icon: 'mdi-check-circle' },
        error: { color: 'error', icon: 'mdi-alert-circle' },
        warning: { color: 'warning', icon: 'mdi-alert' },
        info: { color: 'info', icon: 'mdi-information' }
      };

      this.snackbar = {
        show: true,
        message,
        ...config[type],
        timeout: type === 'error' ? 6000 : 4000
      };
    },

    async init() {
      try {
        // Carrega dados necessários
        await Promise.all([
          this.listarProdutos(),
          this.listarTiposVeiculo(),
        ]);

        // Se há ID na rota, é edição
        const idVeiculo = this.$route.params.id;
        if (idVeiculo) {
          await this.carregarVeiculo(idVeiculo);
        }
      } catch (error) {
        console.error('Erro na inicialização:', error);
        this.mostrarSnackbar('Erro ao carregar dados iniciais', 'error');
      }
    }
  },

  created() {
    this.init();
  }
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.resumo-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

.v-card {
  transition: all 0.3s ease;
}

.v-snackbar {
  font-weight: 500;
}

/* Melhorar visibilidade dos campos obrigatórios */
.v-text-field--outlined >>> .v-label--active {
  color: #1976d2 !important;
}

.v-select--outlined >>> .v-label--active {
  color: #1976d2 !important;
}

/* Destacar campos obrigatórios condicionalmente */
.campo-obrigatorio >>> .v-input__slot {
  border-color: #1976d2 !important;
  border-width: 2px !important;
}

.campo-obrigatorio >>> .v-label {
  color: #1976d2 !important;
  font-weight: 500;
}

/* Estilo para campos com erro */
.error--text >>> .v-input__slot {
  border-color: #ff5252 !important;
}

/* Hover nos botões */
.v-btn:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* Estilo para campos readonly/disabled */
.v-input--is-readonly >>> .v-input__slot {
  background-color: #f5f5f5;
}
</style>